import { familyName } from "pages/EventsManagement/components/Tabs/Logistics/labels"
import { competitionTile, stateLabel } from "./plannerLabels"
import { daysBetween } from "./plannerCalendar"
import { deliveryLabel, sendingMessage } from "./sendingLabels"
import { proposalMessage } from "./proposalMessages"

// The words of publishing (Phase E2p; wayfinder tickets 18 and 25): what Ready to publish, the publish review and its result say. CP
// strings are English.

// The values Ready to publish reads (Phase E1p `values`), by the path a problem or a source names.
export const FIELD_NAMES = {
  name: "Event name", season: "Season", courtType: "Court surface", numberOfCourts: "Courts for the tournament", officialBall: "Official ball",
  coordinator: "Coordinator", directorName: "Tournament director", refereeName: "Referee", supervisorName: "Supervisor",
  singlesMaxNumber: "Singles entries per player", doublesMaxNumber: "Doubles entries per player", description: "Event description",
  playerPackage: "Player package", club: "Club", organizer: "Organizer", competitions: "Competitions", dates: "Dates", event: "The event",
  category: "Category", playMode: "Play mode", sportType: "Sport",
}
const COMPETITION_FIELDS = { level: "level", gameType: "game type", drawModel: "format", numberOfGroups: "groups", mlMaxNumber: "Main List capacity" }

// What a problem's path names: a value, a competition ("6S") or one of its values ("6S format"), a Logistics line's product or supplier.
export const problemSubject = (field, competitions = []) => {
  const [head, key, part] = field.split(".")
  if (head === "competitions" && key) {
    const competition = competitions.find(({ id }) => id === key)
    const tile = competition ? competitionTile(competition) : "A competition"
    return part ? `${tile} ${COMPETITION_FIELDS[part] || part}` : tile
  }
  if (head === "logistics" && part) return `${familyName(part)} ${key === "products" ? "product" : "supplier"}`
  return FIELD_NAMES[field] || field
}

// Why the event creation or the competition editor would refuse it (Phase E1p: their codes on `event` or `competitions.<id>`).
const CREATION = {
  EVENT_CLUB_UNAVAILABLE: "the club is no longer active",
  INVALID_EVENT_ORGANIZER: "the organizer is no longer active or does not organize at this club",
  INVALID_EVENT_COORDINATOR: "the coordinator is no longer in the settings",
  INVALID_EVENT_OFFICIAL_BALL: "the official ball is no longer in the settings",
  INVALID_EVENT_CLASSIFICATION: "the category, play mode, season or court surface is no longer in the settings",
  INVALID_EVENT_COUNTRY: "the platform does not serve this country",
}
const REASONS = {
  missing: () => "missing",
  notInSettings: () => "no longer in the settings",
  notFound: () => "their account no longer exists",
  notAvailable: () => "no longer available for this event",
  noProduct: () => "MPG needs a catalogue product for it",
  tooLong: problem => `it lasts more than ${problem.maxDays || 120} days`,
}
const reasonText = problem => {
  if (REASONS[problem.reason]) return REASONS[problem.reason](problem)
  if (CREATION[problem.reason]) return CREATION[problem.reason]
  if (problem.reason === "INVALID_COMPETITION_DETAILS") {
    const said = Object.values(problem.fieldErrors || {}).filter(Boolean)
    return said.length ? said.join("; ") : "the competition editor refuses it"
  }
  return "the event creation refuses it"
}
// "Complete" or "2 to fix": a proposal of Ready to publish, with its mark's tone.
export const completeness = row => (row.complete ? { label: "Complete", tone: "ok" } : { label: `${row.problems.length} to fix`, tone: "warn" })
// "Coordinator: missing", "6S format: missing", "Dates: it lasts more than 120 days".
export const problemLabel = (problem, competitions) => `${problemSubject(problem.field, competitions)}: ${reasonText(problem)}`

// Where a problem is fixed: what the organizer confirmed (the place, the dates and what classifies the event) only in the editor ("Edit
// proposal", ticket 18 Q1), everything else in the review, on its field.
const CONFIRMED = ["club", "organizer", "dates", "category", "playMode", "sportType"]
export const fixedInEditor = problem => CONFIRMED.includes(problem.field)
  || ["EVENT_CLUB_UNAVAILABLE", "INVALID_EVENT_ORGANIZER", "INVALID_EVENT_COUNTRY", "INVALID_EVENT_CLASSIFICATION"].includes(problem.reason)
  || problem.field === "competitions"

// "in 24 days", "tomorrow", "today", "3 days ago": a start from the country's today.
export const startsIn = (day, today) => {
  const days = daysBetween(today, day)
  if (days > 1) return `in ${days} days`
  if (days === 1) return "tomorrow"
  if (days === 0) return "today"
  return `${-days} ${days === -1 ? "day" : "days"} ago`
}

// The lead-time flag of ticket 13 (Phase C2a `closeToStart`): under 6 weeks (8 for Gold, Platinum and Champions) and not published yet.
export const leadTimeLabel = closeToStart => (!closeToStart ? null : closeToStart.reason === "startPassed" ? "Start passed" : "Close to the start")
export const leadTimeTitle = closeToStart => (!closeToStart ? undefined : closeToStart.reason === "startPassed" ? "Its start has passed."
  : `Starts in ${closeToStart.daysLeft} days (under ${closeToStart.limitDays}) and is not published yet.`)

// Why a proposal of a publish was not published (Phase E1p outcomes), and whether "Fix and publish" takes it back to the review: a
// refusal created nothing for it; a technical failure is retried, which never creates its event twice (the event is named by the
// proposal).
const REFUSED = {
  PROPOSAL_REVISION_CONFLICT: () => "it changed meanwhile, so nothing was created. Reload it and review it again.",
  PROPOSAL_NOT_FOUND: () => "it no longer exists.",
  PROPOSAL_NOT_PUBLISHABLE: details => `it is no longer Confirmed${details?.proposals?.[0]?.state ? ` (${stateLabel(details.proposals[0].state)})` : ""}, so nothing was created.`,
  PROPOSAL_TOO_LONG: details => `it lasts more than ${details?.maxDays || 120} days. Shorten it in the editor.`,
  PROPOSAL_NOT_COMPLETE: () => "something it needs is missing now. Review it again.",
  PROPOSAL_PHASES_PASSED: () => "a phase has passed since the review. Review its phases, then publish it anyway.",
  OPERATION_PAYLOAD_CONFLICT: () => "this publish was already sent with other choices. Review it again.",
}
// The action that takes a refused proposal back to the review: "Reload" for one changed meanwhile (ticket 18 Q2), else "Fix and publish".
export const fixLabel = code => (code === "PROPOSAL_REVISION_CONFLICT" ? "Reload" : "Fix and publish")
export const FIXABLE = ["PROPOSAL_REVISION_CONFLICT", "PROPOSAL_NOT_COMPLETE", "PROPOSAL_PHASES_PASSED", "OPERATION_PAYLOAD_CONFLICT", "PROPOSAL_TOO_LONG"]
export const outcomeLine = outcome => {
  if (outcome.outcome === "published") return `Published as Event #${outcome.eventNumber}`
  if (outcome.outcome === "failed") return "Not published: a technical failure, nothing was created. Retry it: its event is never created twice."
  return `Not published: ${(REFUSED[outcome.message] || (() => "the publish was refused, so nothing was created."))(outcome.details)}`
}

// A publication email (Phase E1p, Tudor's Q1–Q2), per organizer: where it went and how it goes ("To office@aceline.ro · delivered"), or
// why it was not sent; failed or unconfirmed, it is sent again.
export const publicationEmailLine = email => {
  if (!email.publicationId) return "Not emailed: the organizer's account has no address the email may go to. Tell them yourself."
  const to = `To ${email.recipients.join(", ")}`
  return email.delivery ? `${to} · ${deliveryLabel(email.delivery)}` : to
}
export const resendable = email => Boolean(email?.publicationId) && ["failed", "unconfirmed"].includes(email.delivery?.status)
// Still to settle: an email whose delivery is not read yet, or still on its way.
export const settling = email => Boolean(email.publicationId) && (!email.delivery || email.delivery.status === "sending")
const RESEND = {
  PROPOSAL_PUBLICATION_NOT_FOUND: "This publication email no longer exists.",
  INVALID_PROPOSAL_PUBLICATION: "The addresses were refused. Check them, tick “I checked…” again, then send.",
}
export const publicationResendMessage = (code, details) => RESEND[code] || sendingMessage(code, details)

// A value of the review refused (the proposals module's edit, Phase A6), worded for the review: the review is read again after it.
const REVIEW = {
  PROPOSAL_REVISION_CONFLICT: "This proposal changed meanwhile. The review shows its current version: make your change again.",
  PROPOSAL_NOT_EDITABLE: "This proposal can no longer be changed: it was published, declined or withdrawn meanwhile.",
  INVALID_PROPOSAL_FIELDS: "This value was refused. Check it, then change it again.",
}
export const reviewMessage = code => REVIEW[code] || proposalMessage(code)
